import { ALLERGENS, STORAGE_TYPES } from '@felix/domain';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Pagination,
  RingProgress,
  Select,
  Skeleton,
  Tabs,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangleFilled,
  IconArrowsExchange,
  IconBell,
  IconBuildingWarehouse,
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClipboardList,
  IconExclamationCircleFilled,
  IconEye,
  IconFileText,
  IconFilter,
  IconFlag3Filled,
  IconFolderFilled,
  IconPackageExport,
  IconPackageImport,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconShoppingCart,
  IconToolsKitchen2,
} from '@tabler/icons-react';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { followLink, useShell } from '../../shell-context.js';
import { TOKENS } from '../../tokens.js';
import { StockStatusBadge } from '../materials/badges.js';
import { formatMoney, formatQuantity, formatUnit } from '../materials/inventory.js';
import { TableSkeleton } from '../materials/MaterialMasterScreen.js';
import classes from '../materials/Materials.module.css';
import { type SortState, SortTh } from '../materials/SortHeader.js';
import stock from '../materials/Stock.module.css';
import { errorCode } from '../personnel/source.js';
import { RequisitionStatusBadge } from '../purchasing/badges.js';
import { requisitionPath } from '../purchasing/requisitions.js';
import type { RequisitionStatus } from '../purchasing/types.js';
import { ExpiryBadge, type ProvisionAction, ProvisionDetail } from './ProvisionDetail.js';
import {
  CountDialog,
  DisposalDialog,
  IssueDialog,
  LevelsDialog,
  ReceiptDialog,
} from './ProvisionDialogs.js';
import { ProvisionOfficeCards } from './ProvisionOffice.js';
import css from './Provisions.module.css';
import type { ProvisionSource } from './source.js';
import type {
  ProvisionAlertList,
  ProvisionCategory,
  ProvisionDashboard,
  ProvisionItem,
  ProvisionItemList,
  ProvisionMovement,
  ProvisionRequest,
  ProvisionSort,
} from './types.js';
import {
  categoryTree,
  DEFAULT_PROVISION_SORT,
  EMPTY_FILTER,
  type ItemFilter,
  itemsQuery,
  percent,
} from './view.js';

/**
 * Liste sayfa boyutları. Referansta iki liste (envanter 8, kumanya 5 satır) üst üste; kumanyada
 * tek liste ikisinin yerini alır: 10 satır (`gemi-envanter-kumanya.jpeg`; ADR 0099 Ek E3).
 */
const PAGE_SIZES = [10, 20, 50] as const;
const RECENT_ROWS = 4;
const ALERT_ROWS = 4;
/** Purchasing Integration sağ sütunda (referanstaki yer): en çok üç talep satırı. */
const REQUEST_ROWS = 3;
/** Kumanya talebi: talep formu Provisions departmanıyla açılır (ADR 0099; M12b talep formu). */
export const PROVISION_REQUEST_PATH = requisitionPath({ view: 'new', department: 'Provisions' });

type Base =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      dashboard: ProvisionDashboard;
      categories: ProvisionCategory[];
      alerts: ProvisionAlertList;
      movements: ProvisionMovement[];
      requests: ProvisionRequest[] | null;
    };

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: ProvisionItemList };

type Dialog = { action: ProvisionAction; item: ProvisionItem | null; lotId?: string } | null;

export interface ProvisionScreenProps {
  source: ProvisionSource;
}

/**
 * Inventory ▸ Provision (`gemi-envanter-kumanya.jpeg` "Inventory & Provision"; ADR 0099): başlık
 * kartı, özet kutuları, kategori ağacı (PRV kategorileri), kategori sekmeli kumanya listesi ve
 * süzgeçler, stok seviyesi dağılımı ve stok özeti, partiler ve son kullanma, satın alma bağlantısı,
 * son hareketler, uyarılar ve hızlı işlemler. Gemide giriş, FEFO'lu çıkış, imha, sayım ve seviye
 * yetkiye göre; ofiste gemi seçimiyle salt okunur, başlangıç kütüphanesi ve uyarı penceresi ofiste.
 */
export function ProvisionScreen({ source }: ProvisionScreenProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const shell = useShell();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.screen.${key}`, options);
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [base, setBase] = useState<Base>({ kind: 'loading' });
  const [listState, setListState] = useState<ListState>({ kind: 'loading' });
  const [category, setCategory] = useState<{
    categoryCode: string | null;
    categoryId: string | null;
  }>({ categoryCode: null, categoryId: null });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [treeSearch, setTreeSearch] = useState('');
  const [draft, setDraft] = useState<ItemFilter>(EMPTY_FILTER);
  const [filter, setFilter] = useState<ItemFilter>(EMPTY_FILTER);
  const [headSearch, setHeadSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<SortState<ProvisionSort>>(DEFAULT_PROVISION_SORT);
  const [summaryBy, setSummaryBy] = useState<'category' | 'storage'>('category');
  const [detail, setDetail] = useState<ProvisionItem | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const loadBase = useCallback(async () => {
    setBase((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    try {
      const dashboard = await source.dashboard(vesselId);
      const id = dashboard.vessel.id;
      const scoped = source.scope === 'web' ? id : null;
      const [categories, alerts, movements, requests] = await Promise.all([
        source.categories(scoped),
        source.alerts(scoped),
        source.movements({ limit: 50 }, scoped),
        source.requests ? source.requests(id).catch(() => null) : Promise.resolve(null),
      ]);
      setBase({ kind: 'ready', dashboard, categories, alerts, movements, requests });
    } catch (error) {
      setBase({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void version;
    void loadBase();
  }, [loadBase, version]);

  const ready = base.kind === 'ready' ? base : null;
  const dashboard = ready?.dashboard ?? null;
  const scopedVessel = source.scope === 'web' ? (dashboard?.vessel.id ?? null) : null;
  const query = useMemo(
    () =>
      itemsQuery(
        headSearch.trim() ? { ...filter, q: headSearch } : filter,
        category,
        sort,
        page,
        pageSize,
      ),
    [filter, headSearch, category, sort, page, pageSize],
  );
  const dashboardReady = dashboard !== null;
  useEffect(() => {
    void version;
    if (!dashboardReady) return;
    let alive = true;
    // Arama yazılırken her tuşta değil, 250 ms sonra
    const timer = setTimeout(() => {
      setListState((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
      source
        .items(query, scopedVessel)
        .then((list) => {
          if (alive) setListState({ kind: 'ready', list });
        })
        .catch((error) => {
          if (alive) setListState({ kind: 'error', code: errorCode(error) });
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [source, scopedVessel, query, dashboardReady, version]);

  const nodes = useMemo(() => categoryTree(ready?.categories ?? []), [ready]);
  const tabs = useMemo(
    () => nodes.filter((node) => node.categoryCode && node.onBoard > 0),
    [nodes],
  );
  const list = listState.kind === 'ready' ? listState.list : null;
  const rows = list?.total ?? 0;
  const pages = Math.max(1, Math.ceil(rows / pageSize));
  const current = Math.min(page, pages);
  const can = dashboard?.can ?? null;
  const editor = source.editor;
  const today = dashboard?.today ?? '';
  const n = (value: number) => formatQuantity(value, locale);
  const qty = (value: number, unit: string) => `${n(value)} ${formatUnit(unit)}`;
  const movedAt = (move: ProvisionMovement) => formatDateTime(move.createdAt, move.tzOffsetMin);
  const go = (path: string) => shell?.navigate(path);
  const total = dashboard?.totalItems ?? 0;

  const chooseCategory = (categoryCode: string | null, categoryId: string | null = null) => {
    setCategory({ categoryCode, categoryId });
    setPage(1);
  };
  const act = (action: ProvisionAction, item: ProvisionItem | null, lotId?: string) => {
    setNotice(null);
    setDialog({ action, item, ...(lotId ? { lotId } : {}) });
  };
  const saved = async (message: string) => {
    setDialog(null);
    setNotice(message);
    // Ayrıntı açıksa kalemi yeniden okur (eldeki ve durum değişti)
    if (detail) {
      try {
        const fresh = await source.items(
          { q: detail.itemCode ?? detail.name, pageSize: 50 },
          scopedVessel,
        );
        setDetail(fresh.items.find((i) => i.materialId === detail.materialId) ?? detail);
      } catch {
        // Ayrıntı eski değerle kalır; liste yine yenilenir
      }
    }
    setVersion((v) => v + 1);
  };
  const treeMatches = (text: string) =>
    !treeSearch.trim() ||
    text.toLocaleLowerCase(locale).includes(treeSearch.trim().toLocaleLowerCase(locale));
  const sortTh = (column: ProvisionSort, label: string, numeric = false, col?: string) => (
    <SortTh
      column={column}
      label={label}
      sort={sort}
      numeric={numeric}
      col={col}
      onSort={(next) => {
        setSort(next);
        setPage(1);
      }}
    />
  );
  const fact = (label: string, value: ReactNode, icon?: ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>
        {icon ? (
          <span className={stock.factIcon} aria-hidden>
            {icon}
          </span>
        ) : null}
        <span>{value}</span>
      </dd>
    </div>
  );
  const quick = (
    label: string,
    icon: ReactNode,
    tone: 'success' | 'info' | 'purple' | 'warning',
    action: () => void,
    path?: string,
  ) => {
    const body = (
      <>
        <span className={stock.quickIcon} aria-hidden>
          {icon}
        </span>
        <span className={stock.quickLabel}>{label}</span>
      </>
    );
    return path ? (
      <a
        href={path}
        className={stock.quick}
        data-tone={tone}
        data-tinted
        title={label}
        onClick={(event) => {
          if (shell) followLink(event, path, shell.navigate);
        }}
      >
        {body}
      </a>
    ) : (
      <UnstyledButton className={stock.quick} data-tone={tone} title={label} onClick={action}>
        {body}
      </UnstyledButton>
    );
  };
  const alertText = (alert: ProvisionAlertList['alerts'][number]) =>
    t(`provisions.alerts.${alert.kind}`, {
      name: alert.name,
      batch: alert.batchNo ?? t('provisions.dialogs.noBatch'),
      date: formatDay(alert.expiryDate),
      days: alert.daysToExpiry ?? 0,
      quantity: n(alert.quantity),
      unit: formatUnit(alert.unit),
    });
  const openItem = async (stockItemId: string, name: string) => {
    const found = list?.items.find((i) => i.stockItemId === stockItemId);
    if (found) {
      setDetail(found);
      return;
    }
    try {
      const fresh = await source.items({ q: name, pageSize: 50, stockOnly: true }, scopedVessel);
      const item = fresh.items.find((i) => i.stockItemId === stockItemId);
      if (item) setDetail(item);
    } catch {
      // Bulunamazsa ayrıntı açılmaz
    }
  };

  const pager = (
    <div className={stock.pager}>
      <span>
        {k('showing', {
          from: rows === 0 ? 0 : (current - 1) * pageSize + 1,
          to: Math.min(current * pageSize, rows),
          total: rows,
        })}
      </span>
      <div className={classes.pagerEnd}>
        {pages > 1 ? (
          <Pagination
            total={pages}
            value={current}
            onChange={setPage}
            siblings={1}
            aria-label={k('pagination')}
            getControlProps={(control) => ({ 'aria-label': t(`materials.page.${control}`) })}
            getItemProps={(number) => ({
              'aria-label': t('materials.page.number', { page: number }),
            })}
          />
        ) : null}
        {rows > PAGE_SIZES[0] ? (
          <Select
            size="xs"
            className={classes.pageSize}
            aria-label={k('pageSize')}
            data={PAGE_SIZES.map((size) => ({
              value: String(size),
              label: k('perPage', { size }),
            }))}
            value={String(pageSize)}
            allowDeselect={false}
            onChange={(value) => {
              setPageSize(Number(value ?? PAGE_SIZES[0]));
              setPage(1);
            }}
          />
        ) : null}
      </div>
    </div>
  );

  const vessels = dashboard?.vessels ?? [];
  const money = (values: readonly { currency: string; value: number }[] | undefined) =>
    (values ?? []).map((v) => formatMoney(v.value, v.currency, locale)).join(' · ');
  const summaryRows =
    summaryBy === 'category'
      ? (dashboard?.byCategory ?? []).map((row) => ({
          key: row.categoryCode ?? row.mainCategory,
          code: row.categoryCode,
          label: row.mainCategory,
          items: row.items,
          value: money(row.values),
        }))
      : (dashboard?.byStorage ?? []).map((row) => ({
          key: row.storageType ?? '',
          code: null,
          label: row.storageType
            ? t(`provisions.storageTypes.${row.storageType}`, { defaultValue: row.storageType })
            : k('noStorage'),
          items: row.items,
          value: money(row.values),
        }));
  // Value sütunu referanstaki gibi her zaman (ADR 0099 Ek E3); değer yalnız fiyatlı mal kabulü
  // olan kalemden (ADR 0047 §5), yoksa "—". Toplam para birimi başına
  const totalValue = money(
    Object.entries(
      (dashboard?.byCategory ?? [])
        .flatMap((row) => row.values)
        .reduce<Record<string, number>>((sum, v) => {
          sum[v.currency] = (sum[v.currency] ?? 0) + v.value;
          return sum;
        }, {}),
    ).map(([currency, value]) => ({ currency, value })),
  );
  // Referanstaki "Value (USD)": değerler tek para birimindeyse başlıkta
  const currencies = new Set(
    (dashboard?.byCategory ?? []).flatMap((row) => row.values.map((v) => v.currency)),
  );
  const valueCurrency = currencies.size === 1 ? [...currencies][0] : null;
  const flag = dashboard?.flag ?? null;

  return (
    <div className={`${classes.screen} ${stock.inOut} ${css.screen}`}>
      <PageHeader
        title={t('menu.inventoryProvision')}
        titleHidden
        actions={
          source.scope === 'web' && vessels.length > 1 ? (
            <Select
              size="xs"
              aria-label={t('shell.selectVessel')}
              data={vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={dashboard?.vessel.id ?? null}
              allowDeselect={false}
              onChange={(value) => {
                setVesselId(value);
                setCategory({ categoryCode: null, categoryId: null });
                setPage(1);
                setDetail(null);
              }}
            />
          ) : null
        }
      />

      {base.kind === 'error' ? (
        <Panel>
          <EmptyState
            tone="danger"
            title={t(`provisions.errors.${base.code}`, { defaultValue: k('error') })}
            action={
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => void loadBase()}
              >
                {k('retry')}
              </Button>
            }
          />
        </Panel>
      ) : null}
      {notice ? (
        <Alert
          color="green"
          icon={<IconCircleCheck size={18} />}
          withCloseButton
          closeButtonLabel={k('closeNotice')}
          onClose={() => setNotice(null)}
          role="status"
        >
          {notice}
        </Alert>
      ) : null}

      <div className={stock.top}>
        <section className={stock.head} aria-label={k('head')}>
          <div className={stock.headTitle}>
            <span className={stock.headIcon} aria-hidden>
              <IconToolsKitchen2 size={30} stroke={1.5} />
            </span>
            <div className={stock.headText}>
              <strong>{t('provisions.title')}</strong>
              <nav className={stock.headLinks} aria-label={k('sections')}>
                <a
                  href="/inventory/spare-parts"
                  onClick={(event) =>
                    shell && followLink(event, '/inventory/spare-parts', shell.navigate)
                  }
                >
                  {t('menu.inventorySpareParts')}
                </a>
                <span aria-hidden>|</span>
                <a
                  href="/inventory/provision"
                  aria-current="page"
                  onClick={(event) => event.preventDefault()}
                >
                  {t('menu.inventoryProvision')}
                </a>
                <span aria-hidden>|</span>
                {/* Aylık iaşe raporu (ADR 0123) */}
                <a
                  href="/inventory/provision/victualling"
                  onClick={(event) =>
                    shell && followLink(event, '/inventory/provision/victualling', shell.navigate)
                  }
                >
                  {t('menu.inventoryVictualling')}
                </a>
                <span aria-hidden>|</span>
                <a
                  href="/purchasing/requisitions"
                  onClick={(event) =>
                    shell && followLink(event, '/purchasing/requisitions', shell.navigate)
                  }
                >
                  {k('purchasingIntegration')}
                </a>
              </nav>
            </div>
          </div>
          <dl className={stock.headFacts}>
            {fact(k('vessel'), dashboard?.vessel.name ?? '—')}
            {fact(
              k('flag'),
              flag ?? '—',
              flag ? <IconFlag3Filled size={18} className={css.flagIcon} /> : undefined,
            )}
            {fact(k('port'), dashboard?.port ?? '—')}
            {fact(
              k('date'),
              today ? formatDay(today) : '—',
              <IconCalendar size={16} stroke={1.6} />,
            )}
          </dl>
        </section>
        <div className={stock.headActions}>
          <TextInput
            className={stock.headSearch}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={t('provisions.filters.search')}
            aria-label={t('provisions.filters.search')}
            value={headSearch}
            onChange={(event) => {
              setHeadSearch(event.currentTarget.value);
              setPage(1);
            }}
          />
          {editor && can?.create ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => act('in', null)}>
              {k('newRecord')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className={stock.layout}>
        <section className={stock.tiles} aria-label={k('summary')}>
          {dashboard ? (
            <>
              <StatTile
                variant="plain"
                icon={<IconBuildingWarehouse size={22} />}
                label={t('provisions.cards.totalItems')}
                value={n(dashboard.totalItems)}
              />
              <StatTile
                variant="plain"
                icon={<IconAlertTriangleFilled size={20} />}
                label={t('provisions.cards.low')}
                value={n(dashboard.low)}
                tone="warning"
              />
              <StatTile
                variant="plain"
                icon={<IconExclamationCircleFilled size={22} />}
                label={t('provisions.cards.critical')}
                value={n(dashboard.critical)}
                tone="danger"
              />
              <StatTile
                variant="plain"
                icon={<IconPackageImport size={22} />}
                label={t('provisions.cards.todayIn')}
                value={n(dashboard.todayIn)}
                tone="success"
              />
              <StatTile
                variant="plain"
                icon={<IconPackageExport size={22} />}
                label={t('provisions.cards.todayOut')}
                value={n(dashboard.todayOut)}
              />
            </>
          ) : (
            [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={72} radius="md" />)
          )}
        </section>

        <Panel title={k('tree')} className={`${stock.tree} ${css.tree}`}>
          <TextInput
            size="xs"
            leftSection={<IconSearch size={14} aria-hidden />}
            rightSection={<IconFilter size={14} aria-hidden />}
            placeholder={k('treeSearch')}
            aria-label={k('treeSearch')}
            value={treeSearch}
            onChange={(event) => setTreeSearch(event.currentTarget.value)}
          />
          {ready ? (
            <ul className={stock.treeList}>
              <li>
                <div className={stock.treeRow}>
                  <span className={stock.treeToggle} aria-hidden>
                    <IconChevronDown size={14} />
                  </span>
                  <UnstyledButton
                    className={stock.treeNode}
                    aria-pressed={category.categoryCode === null && category.categoryId === null}
                    onClick={() => chooseCategory(null)}
                  >
                    <IconFolderFilled size={16} aria-hidden className={stock.folder} />
                    <span>{k('allProvisions')}</span>
                    <span className={stock.count}>{n(total)}</span>
                  </UnstyledButton>
                </div>
                <ul>
                  {nodes
                    .filter(
                      (node) =>
                        treeMatches(node.mainCategory) ||
                        node.children.some((c) => treeMatches(c.subCategory)),
                    )
                    .map((node) => {
                      const expanded = open.has(node.key) || treeSearch.trim() !== '';
                      return (
                        <li key={node.key}>
                          <div className={stock.treeRow}>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              className={stock.treeToggle}
                              aria-label={k(expanded ? 'collapse' : 'expand', {
                                name: node.mainCategory,
                              })}
                              aria-expanded={expanded}
                              onClick={() =>
                                setOpen((set) => {
                                  const next = new Set(set);
                                  if (next.has(node.key)) next.delete(node.key);
                                  else next.add(node.key);
                                  return next;
                                })
                              }
                            >
                              {expanded ? (
                                <IconChevronDown size={14} />
                              ) : (
                                <IconChevronRight size={14} />
                              )}
                            </ActionIcon>
                            <UnstyledButton
                              className={stock.treeNode}
                              title={node.mainCategory}
                              aria-pressed={
                                category.categoryCode === node.categoryCode &&
                                node.categoryCode !== null &&
                                category.categoryId === null
                              }
                              onClick={() =>
                                node.categoryCode
                                  ? chooseCategory(node.categoryCode)
                                  : chooseCategory(null, node.children[0]?.categoryId ?? null)
                              }
                            >
                              <IconFolderFilled size={16} aria-hidden className={stock.folder} />
                              <span>{node.mainCategory}</span>
                              <span className={stock.count}>{n(node.onBoard)}</span>
                            </UnstyledButton>
                          </div>
                          {expanded ? (
                            <ul>
                              {node.children.map((child) => (
                                <li key={child.categoryId}>
                                  <UnstyledButton
                                    className={stock.treeLeaf}
                                    title={child.subCategory}
                                    aria-pressed={category.categoryId === child.categoryId}
                                    onClick={() =>
                                      chooseCategory(node.categoryCode, child.categoryId)
                                    }
                                  >
                                    <IconFileText size={15} aria-hidden />
                                    <span>{child.subCategory}</span>
                                    <span className={stock.count}>{n(child.onBoard)}</span>
                                  </UnstyledButton>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      );
                    })}
                </ul>
              </li>
            </ul>
          ) : (
            <div className={stock.treeSkeleton} aria-hidden>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={12} width={i === 0 ? '70%' : '55%'} radius="sm" />
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title={t('provisions.sections.list')}
          flush
          className={stock.list}
          extra={
            <Checkbox
              size="xs"
              className={css.onBoard}
              label={t('provisions.filters.stockOnly')}
              checked={draft.stockOnly}
              onChange={(event) => {
                const stockOnly = event.currentTarget.checked;
                setDraft((d) => ({ ...d, stockOnly }));
                setFilter((f) => ({ ...f, stockOnly }));
                setPage(1);
              }}
            />
          }
        >
          <Tabs
            value={category.categoryId ? null : (category.categoryCode ?? 'all')}
            onChange={(value) => chooseCategory(value && value !== 'all' ? value : null)}
            className={`${stock.tabs} ${css.categoryTabs}`}
          >
            <Tabs.List aria-label={k('categoryTabs')}>
              <Tabs.Tab value="all">{k('allItems')}</Tabs.Tab>
              {tabs.map((node) => (
                <Tabs.Tab
                  key={node.key}
                  value={String(node.categoryCode)}
                  title={node.mainCategory}
                >
                  {node.mainCategory}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
          <form
            className={`${stock.listFilters} ${css.filters}`}
            aria-label={k('filters')}
            onSubmit={(event) => {
              event.preventDefault();
              setFilter(draft);
              setPage(1);
            }}
          >
            <Select
              label={t('provisions.columns.storage')}
              data={[
                { value: '', label: k('all') },
                ...STORAGE_TYPES.map((value) => ({
                  value,
                  label: t(`provisions.storageTypes.${value}`),
                })),
              ]}
              value={draft.storageType}
              allowDeselect={false}
              onChange={(value) => setDraft((d) => ({ ...d, storageType: value ?? '' }))}
            />
            <Select
              label={t('provisions.filters.allergen')}
              data={[
                { value: '', label: k('all') },
                ...ALLERGENS.map((value) => ({ value, label: t(`provisions.allergens.${value}`) })),
              ]}
              value={draft.allergen}
              allowDeselect={false}
              onChange={(value) => setDraft((d) => ({ ...d, allergen: value ?? '' }))}
            />
            <Select
              label={t('provisions.columns.status')}
              data={[
                { value: '', label: k('all') },
                ...(['normal', 'low', 'critical'] as const).map((value) => ({
                  value,
                  label: t(`provisions.status.${value}`),
                })),
              ]}
              value={draft.status}
              allowDeselect={false}
              onChange={(value) =>
                setDraft((d) => ({ ...d, status: (value ?? '') as ItemFilter['status'] }))
              }
            />
            <Select
              label={t('provisions.columns.expiry')}
              data={[
                { value: '', label: k('all') },
                { value: 'expiring', label: t('provisions.filters.expiringOnly') },
                { value: 'expired', label: t('provisions.filters.expiredOnly') },
              ]}
              value={draft.expiry}
              allowDeselect={false}
              onChange={(value) =>
                setDraft((d) => ({ ...d, expiry: (value ?? '') as ItemFilter['expiry'] }))
              }
            />
            <TextInput
              aria-label={k('searchItem')}
              placeholder={k('searchItem')}
              leftSection={<IconSearch size={14} aria-hidden />}
              value={draft.q}
              onChange={(event) => {
                const q = event.currentTarget.value;
                setDraft((d) => ({ ...d, q }));
              }}
            />
            <Button type="submit">{k('search')}</Button>
          </form>

          <div className={stock.listBody}>
            {listState.kind === 'error' ? (
              <EmptyState
                tone="danger"
                title={t(`provisions.errors.${listState.code}`, { defaultValue: k('error') })}
              />
            ) : !list ? (
              <div className={stock.tableBox}>
                <TableSkeleton columns={9} rows={PAGE_SIZES[0]} />
              </div>
            ) : list.items.length === 0 ? (
              <EmptyState
                icon={<IconSearch size={22} stroke={1.8} />}
                title={
                  total === 0 && !filter.q && !headSearch
                    ? k('emptyTitle')
                    : t('provisions.empty.list')
                }
                description={
                  total === 0 && !filter.q && !headSearch
                    ? editor
                      ? k('emptyShip')
                      : k('emptyWeb')
                    : undefined
                }
              />
            ) : (
              <section
                className={`${classes.tableWrap} ${stock.tableBox}`}
                aria-label={t('provisions.sections.list')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${classes.table} ${css.itemTable}`}>
                  <thead>
                    <tr>
                      <th scope="col" data-col="code">
                        {t('provisions.columns.itemCode')}
                      </th>
                      {sortTh('name', t('provisions.columns.item'))}
                      {sortTh('category', t('provisions.columns.category'), false, 'category')}
                      <th scope="col">{t('provisions.columns.unit')}</th>
                      <th scope="col" data-col="storage">
                        {t('provisions.columns.storage')}
                      </th>
                      <th scope="col" data-col="location">
                        {t('provisions.columns.location')}
                      </th>
                      {sortTh('onHand', t('provisions.columns.onHand'), true)}
                      <th scope="col" data-numeric data-col="min">
                        {t('provisions.columns.min')}
                      </th>
                      {sortTh('status', t('provisions.columns.status'))}
                      {sortTh('expiry', t('provisions.columns.nextExpiry'), false, 'expiry')}
                      <th scope="col" data-col="actions">
                        <span className={css.visuallyHidden}>
                          {t('provisions.columns.actions')}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.items.map((item) => (
                      <tr key={item.materialId} data-inactive={item.active ? undefined : true}>
                        <td data-col="code" className={css.code}>
                          {item.itemCode ?? '—'}
                        </td>
                        <td>
                          <UnstyledButton
                            className={stock.linkButton}
                            aria-haspopup="dialog"
                            title={[item.name, item.specification].filter(Boolean).join(' · ')}
                            onClick={() => setDetail(item)}
                          >
                            {item.name}
                          </UnstyledButton>
                          {/* Dar kapta kod sütunu gizlenir; kod adın altında. Özellik `title`'da */}
                          {item.itemCode ? (
                            <span className={css.inlineCode}>{item.itemCode}</span>
                          ) : null}
                        </td>
                        <td data-col="category" title={item.mainCategory}>
                          {item.subCategory}
                        </td>
                        <td>{formatUnit(item.unit)}</td>
                        <td data-col="storage">
                          {item.storageType
                            ? t(`provisions.storageTypes.${item.storageType}`, {
                                defaultValue: item.storageType,
                              })
                            : '—'}
                        </td>
                        <td data-col="location">{item.location ?? '—'}</td>
                        <td data-numeric>{n(item.onHand)}</td>
                        <td data-numeric data-col="min">
                          {item.minQty === null ? '—' : n(item.minQty)}
                        </td>
                        <td>
                          {item.status ? (
                            <StockStatusBadge status={item.status} />
                          ) : (
                            <span className={classes.muted}>
                              {t('provisions.status.notOnBoard')}
                            </span>
                          )}
                        </td>
                        <td data-col="expiry">
                          <ExpiryBadge state={item.expiryState} date={item.nextExpiry} />
                        </td>
                        <td className={stock.actions} data-col="actions">
                          <ActionIcon
                            variant="default"
                            size="sm"
                            aria-label={k('openItem', { name: item.name })}
                            onClick={() => setDetail(item)}
                          >
                            <IconEye size={15} />
                          </ActionIcon>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
          {list && rows > 0 ? pager : null}
        </Panel>

        <div className={stock.side}>
          <Panel title={t('provisions.sections.distribution')} className={stock.distribution}>
            <div className={stock.ring}>
              {dashboard ? (
                <RingProgress
                  size={100}
                  thickness={12}
                  roundCaps={false}
                  sections={
                    total === 0
                      ? [{ value: 100, color: TOKENS['page-bg'] }]
                      : [
                          {
                            value: (dashboard.normal / total) * 100,
                            color: TOKENS['success-fg'],
                          },
                          { value: (dashboard.low / total) * 100, color: TOKENS['warning-500'] },
                          {
                            value: (dashboard.critical / total) * 100,
                            color: TOKENS['danger-fg'],
                          },
                        ]
                  }
                  label={<strong className={stock.ringValue}>{n(total)}</strong>}
                  aria-hidden
                />
              ) : (
                <Skeleton height={100} width={100} circle />
              )}
              <ul className={stock.legend}>
                {(['normal', 'low', 'critical'] as const).map((status) => (
                  <li key={status}>
                    <span className={stock.legendDot} data-status={status} aria-hidden />
                    <span className={stock.legendLabel}>{t(`provisions.status.${status}`)}</span>
                    <span className={classes.number}>
                      {dashboard
                        ? `${n(dashboard[status])} (${percent(dashboard[status], total)}%)`
                        : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>

          <Panel
            title={t('provisions.sections.summary')}
            className={`${stock.summary} ${css.summaryCard}`}
            extra={
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={k('refresh')}
                onClick={() => setVersion((v) => v + 1)}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            }
          >
            <Tabs
              value={summaryBy}
              onChange={(value) => setSummaryBy((value ?? 'category') as 'category' | 'storage')}
              className={stock.summaryTabs}
            >
              <Tabs.List>
                <Tabs.Tab value="category">{k('byCategory')}</Tabs.Tab>
                <Tabs.Tab value="storage">{k('byStorage')}</Tabs.Tab>
              </Tabs.List>
            </Tabs>
            {!dashboard ? (
              <Skeleton height={160} radius="sm" />
            ) : summaryRows.length === 0 ? (
              <p className={classes.muted}>{k('noSummary')}</p>
            ) : (
              <section
                className={`${stock.miniBox} ${css.summaryBox}`}
                aria-label={k('summaryTable')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${classes.miniTable} ${css.summaryTable}`}>
                  <thead>
                    <tr>
                      <th scope="col">
                        {summaryBy === 'category'
                          ? t('provisions.columns.category')
                          : t('provisions.columns.storage')}
                      </th>
                      <th scope="col" data-numeric>
                        {k('items')}
                      </th>
                      <th scope="col" data-numeric data-col="value">
                        {valueCurrency
                          ? k('valueIn', { currency: valueCurrency })
                          : t('provisions.columns.value')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((row) => (
                      <tr key={row.key || '-'}>
                        <td>
                          {row.code ? (
                            <UnstyledButton
                              className={stock.linkButton}
                              title={row.label}
                              onClick={() => chooseCategory(row.code)}
                            >
                              {row.label}
                            </UnstyledButton>
                          ) : (
                            row.label
                          )}
                        </td>
                        <td data-numeric>{n(row.items)}</td>
                        <td data-numeric>{row.value || '—'}</td>
                      </tr>
                    ))}
                    <tr className={stock.totalRow}>
                      <td>{k('totalRow')}</td>
                      <td data-numeric>{n(total)}</td>
                      <td data-numeric>{totalValue || '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            )}
          </Panel>

          <Panel
            title={k('purchasingIntegration')}
            icon={<IconShoppingCart size={18} />}
            className={`${css.purchasing} ${css.sideCard}`}
            extra={
              <Button
                size="compact-xs"
                variant="outline"
                component="a"
                href="/purchasing/requisitions"
                onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                  if (shell) followLink(event, '/purchasing/requisitions', shell.navigate);
                }}
              >
                {k('viewRequests')}
              </Button>
            }
          >
            {!ready ? (
              <Skeleton height={120} radius="sm" />
            ) : ready.requests === null ? (
              <p className={classes.muted}>{k('requestsUnavailable')}</p>
            ) : ready.requests.length === 0 ? (
              <p className={classes.muted}>{k('noRequests')}</p>
            ) : (
              <section
                className={`${stock.scroll} ${stock.miniBox}`}
                aria-label={k('requestsTable')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${classes.miniTable} ${stock.recentTable}`}>
                  <thead>
                    <tr>
                      <th scope="col">{k('requestNo')}</th>
                      <th scope="col">{k('requiredDate')}</th>
                      <th scope="col" data-numeric>
                        {k('lines')}
                      </th>
                      <th scope="col">{t('provisions.columns.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ready.requests.slice(0, REQUEST_ROWS).map((request) => (
                      <tr key={request.id}>
                        <td>
                          <a
                            className={stock.link}
                            href={`/purchasing/requisitions?id=${request.id}`}
                            onClick={(event) => {
                              if (shell) {
                                followLink(
                                  event,
                                  `/purchasing/requisitions?id=${request.id}`,
                                  shell.navigate,
                                );
                              }
                            }}
                          >
                            {request.requisitionNo}
                          </a>
                        </td>
                        <td>{formatDay(request.requiredDate)}</td>
                        <td data-numeric>{n(request.lineCount)}</td>
                        <td>
                          <RequisitionStatusBadge status={request.status as RequisitionStatus} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
            {editor && can?.requisition ? (
              <Button
                size="xs"
                className={css.requestButton}
                leftSection={<IconPlus size={14} />}
                onClick={() => go(PROVISION_REQUEST_PATH)}
              >
                {t('provisions.actions.request')}
              </Button>
            ) : null}
          </Panel>
        </div>
      </div>

      {source.office && dashboard ? (
        <ProvisionOfficeCards
          office={source.office}
          can={dashboard.can}
          onChanged={() => setVersion((v) => v + 1)}
        />
      ) : null}

      <div className={stock.bottom}>
        <Panel
          title={t('provisions.sections.movements')}
          icon={<IconArrowsExchange size={18} />}
          className={stock.bottomCard}
        >
          {!ready ? (
            <Skeleton height={120} radius="sm" />
          ) : ready.movements.length === 0 ? (
            <p className={classes.muted}>{t('provisions.empty.movements')}</p>
          ) : (
            <section
              className={`${stock.scroll} ${stock.miniBox}`}
              aria-label={k('recentTable')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table
                className={`${classes.miniTable} ${stock.recentTable} ${css.recentProvisions}`}
              >
                <thead>
                  <tr>
                    <th scope="col">{t('provisions.columns.date')}</th>
                    <th scope="col">{t('provisions.columns.type')}</th>
                    <th scope="col">{t('provisions.columns.item')}</th>
                    <th scope="col" data-numeric>
                      {t('provisions.columns.qty')}
                    </th>
                    <th scope="col">{t('provisions.columns.batch')}</th>
                    <th scope="col">{t('provisions.columns.user')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ready.movements.slice(0, RECENT_ROWS).map((move) => (
                    <tr key={move.id}>
                      <td className={stock.link}>{movedAt(move)}</td>
                      <td>{t(`provisions.kinds.${move.kind}`, { defaultValue: move.kind })}</td>
                      <td>
                        <UnstyledButton
                          className={stock.linkButton}
                          title={move.name}
                          onClick={() =>
                            void openItem(move.stockItemId, move.itemCode ?? move.name)
                          }
                        >
                          {move.name}
                        </UnstyledButton>
                      </td>
                      <td data-numeric>{qty(Math.abs(move.quantity), move.unit)}</td>
                      <td title={move.batchNo ?? undefined}>{move.batchNo ?? '—'}</td>
                      <td title={move.createdByName ?? undefined}>{move.createdByName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </Panel>

        <Panel
          title={t('provisions.sections.alerts')}
          icon={<IconBell size={18} />}
          className={stock.bottomCard}
          extra={
            ready && ready.alerts.alerts.length > 0 ? (
              <Button
                size="compact-xs"
                variant="outline"
                onClick={() => {
                  const next = { ...EMPTY_FILTER, expiry: 'expiring' as const };
                  setCategory({ categoryCode: null, categoryId: null });
                  setDraft(next);
                  setFilter(next);
                  setSort({ column: 'expiry', direction: 'asc' });
                  setPage(1);
                }}
              >
                {k('viewAll')}
              </Button>
            ) : null
          }
        >
          {!ready ? (
            <Skeleton height={120} radius="sm" />
          ) : ready.alerts.alerts.length === 0 ? (
            <p className={classes.muted}>{t('provisions.alerts.empty')}</p>
          ) : (
            <ul className={stock.alertList}>
              {ready.alerts.alerts.slice(0, ALERT_ROWS).map((alert) => {
                const text = alertText(alert);
                const danger = alert.kind === 'expired' || alert.kind === 'critical';
                return (
                  <li key={`${alert.kind}-${alert.lotId ?? alert.stockItemId}`}>
                    <span
                      className={stock.alertIcon}
                      data-tone={danger ? 'critical' : 'low'}
                      aria-hidden
                    >
                      {danger ? (
                        <IconExclamationCircleFilled size={20} />
                      ) : (
                        <IconAlertTriangleFilled size={18} />
                      )}
                    </span>
                    <UnstyledButton
                      className={stock.alertText}
                      title={text}
                      onClick={() => void openItem(alert.stockItemId, alert.name)}
                    >
                      {text}
                    </UnstyledButton>
                    <span className={stock.alertTime}>
                      {alert.expiryDate
                        ? formatDay(alert.expiryDate)
                        : t(`provisions.status.${alert.kind}`)}
                    </span>
                    <IconChevronRight size={14} aria-hidden className={stock.alertChevron} />
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel
          title={t('provisions.sections.quickActions')}
          icon={<IconClipboardList size={18} />}
          className={stock.bottomCard}
        >
          <div className={stock.quickGrid}>
            {editor && can?.create ? (
              <>
                {quick(
                  t('provisions.actions.stockIn'),
                  <IconPackageImport size={17} />,
                  'success',
                  () => act('in', null),
                )}
                {quick(
                  t('provisions.actions.stockOut'),
                  <IconPackageExport size={17} />,
                  'info',
                  () => act('out', null),
                )}
              </>
            ) : null}
            {editor && can?.requisition
              ? quick(
                  t('provisions.actions.request'),
                  <IconToolsKitchen2 size={17} />,
                  'purple',
                  () => go(PROVISION_REQUEST_PATH),
                  PROVISION_REQUEST_PATH,
                )
              : null}
            {quick(
              t('provisions.actions.viewRequests'),
              <IconShoppingCart size={17} />,
              'warning',
              () => go('/purchasing/requisitions'),
              '/purchasing/requisitions',
            )}
          </div>
          {editor ? null : <p className={classes.muted}>{t('provisions.hints.officeReadOnly')}</p>}
        </Panel>
      </div>

      <ProvisionDetail
        item={detail}
        source={source}
        vesselId={scopedVessel}
        can={can}
        version={version}
        {...(dashboard ? { expiryWarningDays: dashboard.expiryWarningDays } : {})}
        onAction={(action, item, lotId) => act(action, item, lotId)}
        onClose={() => setDetail(null)}
      />

      {editor && dashboard ? (
        <>
          <ReceiptDialog
            opened={dialog?.action === 'in'}
            item={dialog?.action === 'in' ? dialog.item : null}
            source={source}
            editor={editor}
            vesselId={scopedVessel}
            today={today}
            onClose={() => setDialog(null)}
            onSaved={(message) => void saved(message)}
          />
          <IssueDialog
            opened={dialog?.action === 'out'}
            item={dialog?.action === 'out' ? dialog.item : null}
            source={source}
            editor={editor}
            vesselId={scopedVessel}
            today={today}
            onClose={() => setDialog(null)}
            onSaved={(message) => void saved(message)}
          />
          {dialog?.action === 'dispose' && dialog.item ? (
            <DisposalDialog
              opened
              item={dialog.item}
              lotId={dialog.lotId ?? null}
              source={source}
              editor={editor}
              vesselId={scopedVessel}
              today={today}
              onClose={() => setDialog(null)}
              onSaved={(message) => void saved(message)}
            />
          ) : null}
          {dialog?.action === 'count' && dialog.item ? (
            <CountDialog
              opened
              item={dialog.item}
              source={source}
              editor={editor}
              vesselId={scopedVessel}
              today={today}
              onClose={() => setDialog(null)}
              onSaved={(message) => void saved(message)}
            />
          ) : null}
          {dialog?.action === 'levels' && dialog.item ? (
            <LevelsDialog
              opened
              item={dialog.item}
              editor={editor}
              vesselId={scopedVessel}
              today={today}
              onClose={() => setDialog(null)}
              onSaved={(message) => void saved(message)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
